// AdsBitVex rewarded video ad integration.
// Docs: https://adsbitvex.com/  (SDK is a browser global that exposes
// `window.showadsbitvex()` returning a Promise which resolves when the user
// closes the ad after watching, and rejects when no ad could be served
// (no-fill) or on error.
//
// We lazy-load the SDK script the first time an ad is requested and reuse
// the promise on subsequent calls. When REACT_APP_ADSBITVEX_APP_ID is empty
// (local dev without production creds) we fall back to a 5-second demo
// countdown so the whole flow keeps working.
//
// Design notes per product requirements:
//   • No app-side watchdog — trust the SDK. If SDK never resolves the user
//     can dismiss its own player. When SDK errors ("no ads right now") the
//     Promise rejects and we simply close without granting a reward.
//   • Immediate play — playRewardedAd() calls window.showadsbitvex() on the
//     first tick after SDK ready; the caller renders no interstitial UI.
//   • Reward on close — SDK's Promise resolves when the user closes the ad
//     AFTER watching; that's the exact moment we grant the reward.

const ADSBITVEX_APP_ID = (process.env.REACT_APP_ADSBITVEX_APP_ID || "").trim();
const ADSBITVEX_SDK_URL = ADSBITVEX_APP_ID
  ? `https://sdk.adsbitvex.com/functions/v1/ad-script?appid=${encodeURIComponent(ADSBITVEX_APP_ID)}`
  : "";
const DEMO_MOCK_DURATION_MS = 5000;

let sdkLoadPromise = null;

function loadAdsBitVexSdk() {
  if (typeof window === "undefined") {
    return Promise.reject(new Error("AdsBitVex SDK: no window"));
  }
  if (typeof window.showadsbitvex === "function") {
    return Promise.resolve();
  }
  if (sdkLoadPromise) return sdkLoadPromise;
  sdkLoadPromise = new Promise((resolve, reject) => {
    const s = document.createElement("script");
    s.src = ADSBITVEX_SDK_URL;
    s.async = true;
    s.onload = () => {
      if (typeof window.showadsbitvex === "function") {
        resolve();
      } else {
        sdkLoadPromise = null;
        reject(new Error("AdsBitVex SDK loaded but showadsbitvex missing"));
      }
    };
    s.onerror = () => {
      sdkLoadPromise = null;
      reject(new Error("AdsBitVex SDK script failed to load"));
    };
    document.head.appendChild(s);
  });
  return sdkLoadPromise;
}

/** True when a real AdsBitVex App ID is wired in — flip the mock off. */
export function isAdsConfigured() {
  return ADSBITVEX_APP_ID.length > 0;
}

/**
 * Show a rewarded ad. Resolves when the user watched the ad AND closed it
 * (grant reward). Rejects when no ad was available ("no ads right now"),
 * when the SDK fails to load, or on any other error. Callers should treat
 * reject as a plain "close without reward" — do NOT show a scary error UI.
 * When no App ID is configured, falls back to a 5-second demo countdown so
 * local dev flows keep working.
 */
export function playRewardedAd() {
  // Dev / no-key fallback.
  if (!isAdsConfigured()) {
    return new Promise((resolve) => setTimeout(resolve, DEMO_MOCK_DURATION_MS));
  }
  return loadAdsBitVexSdk().then(() => {
    // window.showadsbitvex() itself returns a Promise per the AdsBitVex docs.
    return window.showadsbitvex();
  });
}
